import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EHumanReviewRequestProblem, EHumanReviewResolution } from './human-review-requests.type'
import {
  parseEnumList,
  parsePositiveInteger,
  parsePositiveIntegerList,
} from './human-review-search-params.util'

type TFiltersUpdate = {
  problems?: EHumanReviewRequestProblem[]
  resolutions?: EHumanReviewResolution[]
  medEventIds?: number[]
}

// Filters are `null` when absent from the URL (meaning "default selection") and `[]` when explicitly emptied (`?problems=`)
export const useHumanReviewSelection = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedPatientMedEventId = parsePositiveInteger(searchParams.get('patientMedEventId'))

  const problemsFilter = useMemo(
    () => parseEnumList(EHumanReviewRequestProblem, searchParams.get('problems')),
    [searchParams],
  )

  const resolutionsFilter = useMemo(
    () => parseEnumList(EHumanReviewResolution, searchParams.get('resolutions')),
    [searchParams],
  )

  const medEventIdsFilter = useMemo(
    () => parsePositiveIntegerList(searchParams.get('medEvents')),
    [searchParams],
  )

  const selectPatientMedEvent = useCallback(
    (patientMedEventId: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('patientMedEventId', patientMedEventId.toString())
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const updateFilters = ({ problems, resolutions, medEventIds }: TFiltersUpdate) => {
    const newParams = new URLSearchParams(searchParams)
    if (problems) newParams.set('problems', problems.join(','))
    if (resolutions) newParams.set('resolutions', resolutions.join(','))
    if (medEventIds) newParams.set('medEvents', medEventIds.join(','))
    setSearchParams(newParams, { replace: true })
  }

  return {
    selectedPatientMedEventId,
    problemsFilter,
    resolutionsFilter,
    medEventIdsFilter,
    selectPatientMedEvent,
    updateFilters,
  }
}
